/**
 * FlatListView — virtualized flat table body shared by App RuntimeListView and
 * Designer PredefinedTab. Same ListHeaderCell + ListDataRow chrome as App.
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { ListColumnSpec } from '@/runtime/listSchema'
import type { ListCellAttrHint } from '@/shared/formatListCellValue'
import { ListDataRow, type ListRowPrint } from '@/runtime/ListDataRow'
import { ListHeaderCell } from '@/runtime/ListHeaderCell'
import type { AttributeDef } from '@/designer/services/configObjectApi'
import { LIST_ROW_OVERFLOW_COL_WIDTH } from '@/shared/listTable'
import { listCellHairlineSx, listHeaderStickySx } from '@/shared/listTable/listTableChrome'
import { ListEmptyState } from './ListEmptyState'

/** Mirrors runtime `RowState` so App can pass `useWindowedRows().getRow` directly. */
export type FlatListRowCell =
  | { state: 'loaded'; row: Record<string, any> }
  | { state: 'loading' }
  | { state: 'placeholder' }

export interface FlatListViewHandle {
  scrollToIndex: (index: number, opts?: { align?: 'auto' | 'start' | 'center' | 'end' }) => void
}

export interface FlatListViewProps {
  count: number
  getRow: (index: number) => FlatListRowCell
  columns: ListColumnSpec[]
  hintFor: (col: ListColumnSpec) => ListCellAttrHint | undefined
  rowHeight: number
  print: ListRowPrint
  onEdit: (row: Record<string, any>) => void
  onDelete: (recordId: string, wasMarked?: boolean) => void
  onUndelete: (recordId: string) => void
  loading?: boolean
  error?: string | null
  emptyMessage?: string
  /** Selection column (App flat list). */
  selectable?: boolean
  isSelected?: (rowId: string) => boolean
  onToggleSelect?: (e: React.MouseEvent, index: number) => void
  selectAllChecked?: boolean
  selectAllIndeterminate?: boolean
  onToggleSelectAll?: () => void
  /** Keyboard-nav active row index. */
  activeIndex?: number
  onKeyDown?: (e: React.KeyboardEvent) => void
  /** Header sort/resize/reorder. */
  sortField?: string
  sortDir?: 'asc' | 'desc'
  onSort?: (field: string) => void
  onResize?: (name: string, width: number) => void
  onResetWidth?: (name: string) => void
  onReorder?: (from: string, to: string) => void
  headerInteractive?: boolean
  /** Inline cell editing (App). Off for Designer Predefined. */
  editable?: boolean
  connectionId?: string
  attrFor?: (col: ListColumnSpec) => AttributeDef | undefined
  editState?: { rowId: string; field: string } | null
  editDraft?: unknown
  onBeginEdit?: (rowId: string, field: string, current: unknown) => void
  onEditChange?: (v: unknown) => void
  onEditCommit?: () => void
  onEditCancel?: () => void
  allowDeletePredefined?: boolean
  /** Effective delete permission (Task 12). Defaults to true — see ListDataRow. */
  canDelete?: boolean
  kindIconColumn?: string | null
  leadingForRow?: (row: Record<string, any>) => React.ReactNode
}

export const FlatListView = forwardRef<FlatListViewHandle, FlatListViewProps>(function FlatListView(
  {
    count,
    getRow,
    columns,
    hintFor,
    rowHeight,
    print,
    onEdit,
    onDelete,
    onUndelete,
    loading,
    error,
    emptyMessage = 'No records found',
    selectable = false,
    isSelected,
    onToggleSelect,
    selectAllChecked = false,
    selectAllIndeterminate = false,
    onToggleSelectAll,
    activeIndex = -1,
    onKeyDown,
    sortField = '',
    sortDir = 'asc',
    onSort,
    onResize,
    onResetWidth,
    onReorder,
    headerInteractive = true,
    editable = false,
    connectionId,
    attrFor,
    editState,
    editDraft,
    onBeginEdit,
    onEditChange,
    onEditCommit,
    onEditCancel,
    allowDeletePredefined = false,
    canDelete = true,
    kindIconColumn,
    leadingForRow,
  },
  ref,
) {
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  })

  useEffect(() => {
    virtualizer.measure()
  }, [rowHeight, virtualizer])

  useImperativeHandle(
    ref,
    () => ({
      scrollToIndex: (index, opts) => virtualizer.scrollToIndex(index, opts),
    }),
    [virtualizer],
  )

  const virtualItems = virtualizer.getVirtualItems()
  const totalSize = virtualizer.getTotalSize()
  const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
  const paddingBottom =
    virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0
  const colSpan = columns.length + 1 + (selectable ? 1 : 0)

  const renderSkeletonRow = (key: React.Key) => (
    <TableRow key={key} sx={{ height: rowHeight }}>
      {selectable && <TableCell padding="checkbox" sx={listCellHairlineSx} />}
      {columns.map((col) => (
        <TableCell key={col.name} sx={listCellHairlineSx}>
          <Skeleton variant="text" width="80%" />
        </TableCell>
      ))}
      <TableCell align="right" sx={listCellHairlineSx}>
        <Skeleton variant="text" width="60%" />
      </TableCell>
    </TableRow>
  )

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, gap: 1 }}>
      {error && <Alert severity="error">{error}</Alert>}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ flex: 1, overflow: 'auto' }}
        ref={scrollRef as React.Ref<HTMLDivElement>}
        tabIndex={onKeyDown ? 0 : undefined}
        onKeyDown={onKeyDown}
      >
        <Table size="small" stickyHeader sx={{ tableLayout: 'auto' }}>
          <TableHead>
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox" sx={{ ...listHeaderStickySx, ...listCellHairlineSx }}>
                  <Checkbox
                    size="small"
                    indeterminate={selectAllIndeterminate}
                    checked={selectAllChecked}
                    onChange={onToggleSelectAll}
                    slotProps={{ input: { 'aria-label': 'Select all rows' } }}
                  />
                </TableCell>
              )}
              {columns.map((col) =>
                headerInteractive && onSort && onResize && onResetWidth && onReorder ? (
                  <ListHeaderCell
                    key={col.name}
                    col={col}
                    sortActive={sortField === col.name}
                    sortDir={sortDir}
                    onSort={() => onSort(col.name)}
                    onResize={onResize}
                    onResetWidth={onResetWidth}
                    onReorder={onReorder}
                  />
                ) : (
                  <TableCell key={col.name} sx={{ ...listHeaderStickySx, ...listCellHairlineSx }}>
                    {col.title || col.name}
                  </TableCell>
                ),
              )}
              <TableCell
                align="right"
                aria-label="Row actions"
                sx={{ width: LIST_ROW_OVERFLOW_COL_WIDTH, ...listHeaderStickySx, ...listCellHairlineSx, textAlign: 'right' }}
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && count === 0 ? (
              Array.from({ length: 4 }, (_, i) => renderSkeletonRow(`s-${i}`))
            ) : (
              <>
                {paddingTop > 0 && (
                  <TableRow style={{ height: paddingTop }}>
                    <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }} />
                  </TableRow>
                )}

                {virtualItems.map((virtualRow) => {
                  const cell = getRow(virtualRow.index)
                  if (cell.state !== 'loaded') {
                    return renderSkeletonRow(`vk-${virtualRow.key}`)
                  }
                  const row = cell.row
                  return (
                    <ListDataRow
                      key={`${virtualRow.key}-${row.id}`}
                      row={row}
                      columns={columns}
                      hintFor={hintFor}
                      rowHeight={rowHeight}
                      print={print}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onUndelete={onUndelete}
                      active={virtualRow.index === activeIndex}
                      selectable={selectable}
                      selected={isSelected?.(String(row.id))}
                      rowIndex={virtualRow.index}
                      onToggleSelect={onToggleSelect}
                      editable={editable}
                      connectionId={connectionId}
                      attrFor={attrFor}
                      editState={editState}
                      editDraft={editDraft}
                      onBeginEdit={onBeginEdit}
                      onEditChange={onEditChange}
                      onEditCommit={onEditCommit}
                      onEditCancel={onEditCancel}
                      allowDeletePredefined={allowDeletePredefined}
                      canDelete={canDelete}
                      kindIconColumn={kindIconColumn}
                      leading={leadingForRow?.(row)}
                    />
                  )
                })}

                {paddingBottom > 0 && (
                  <TableRow style={{ height: paddingBottom }}>
                    <TableCell colSpan={colSpan} sx={{ p: 0, border: 0 }} />
                  </TableRow>
                )}

                {!loading && count === 0 && (
                  <TableRow>
                    <TableCell colSpan={colSpan} align="center" sx={{ py: 0, border: 0 }}>
                      <ListEmptyState message={emptyMessage} />
                    </TableCell>
                  </TableRow>
                )}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
})
